import { useState, useRef, useEffect, useCallback } from 'react'
import { dia } from '@joint/core'

import { useFloorStore } from '../../../../map-editor-shared/store/floorStore'
import { useCsvRuntimeStore } from '../../../../map-editor-features/csv/model/csvStore'
import {
  mapDataGetCsvFileName,
  mapDataGetCsvGroupedLayers,
  mapDataGetCsvParsed,
  mapDataGetCsvRaw,
  mapDataGetCsvSelectedLayers,
} from '../../../../map-editor-shared/store/mapDataCsvHelpers'
import {
  checkHasLocalData,
  findMatchingApiFloor,
  type ApiMapDataLike,
} from '../../../../map-editor-shared/lib/graphJsonLocalData'
import { normalizeMapScale } from '../../../../map-editor-features/editor/lib/normalizeMapScale'

interface UseFloorSwitchingProps {
  graph: dia.Graph | null
  paper: dia.Paper | null
  currentLot: string
  editorMapData: ApiMapDataLike | null
  onFloorSwitchStart?: () => void
  onFloorSwitchComplete?: () => void
  setObjectsByLayer?: (layers: Map<string, dia.Element[]>) => void
  setElementCount?: (count: number) => void
}

interface UseFloorSwitchingReturn {
  isRestoring: boolean
  renderScale: number
  loadedFileName: string | null
  handleRenderScaleChange: (newScale: number) => void
  setRenderScale: React.Dispatch<React.SetStateAction<number>>
}

export function useFloorSwitching(props: UseFloorSwitchingProps): UseFloorSwitchingReturn {
  const {
    graph,
    paper,
    currentLot,
    editorMapData,
    onFloorSwitchStart,
    onFloorSwitchComplete,
  } = props

  const {
    currentFloor,
    updateFloorMapData,
    setFloorSwitching,
    isFloorSwitching,
  } = useFloorStore()

  // ======================
  // Refs for tracking state without triggering re-renders
  // ======================
  const previousFloorRef = useRef<string | null>(null)
  const previousLotRef = useRef<string | null>(null)
  const isFloorSwitchingRef = useRef<boolean>(false)
  const floorRestorationIdRef = useRef<number>(0)

  // ======================
  // State
  // ======================
  const [isRestoring, setIsRestoring] = useState(false)
  const [renderScale, setRenderScale] = useState(1500)
  const [loadedFileName, setLoadedFileName] = useState<string | null>(null)

  // ======================
  // Handler: Render scale change
  // ======================
  const handleRenderScaleChange = useCallback(
    (newScale: number) => {
      setRenderScale(newScale)

      // Save to floor store
      if (currentFloor) {
        updateFloorMapData(currentFloor, { renderScale: newScale })
      }
    },
    [currentFloor, updateFloorMapData]
  )

  // ======================
  // Effect: Main floor switching logic
  // ======================
  useEffect(() => {
    // Skip if no floor selected
    if (!currentFloor) {
      return
    }

    // Detect lot change
    const lotChanged = currentLot !== previousLotRef.current
    if (lotChanged) {
      previousLotRef.current = currentLot
      previousFloorRef.current = null
    }

    // Detect floor change
    const floorChanged = currentFloor !== previousFloorRef.current

    // Re-run restoration when API map_data arrives after an empty local restore (same floor/lot).
    // Handles two cases:
    // 1. Store has no data yet, but API has data → need to sync + restore
    // 2. Store already has data (useFloorSelection synced it), but canvas is empty → need to restore
    const lateApiGraphShouldRestore =
      !floorChanged &&
      !lotChanged &&
      Boolean(editorMapData?.floors?.length) &&
      (() => {
        const fresh = useFloorStore.getState().floors.find((f) => f.id === currentFloor)
        if (!fresh || String(fresh.lotId) !== String(currentLot)) return false

        const storeHasData = checkHasLocalData(fresh.mapData?.graphJson)
        const canvasIsEmpty = !graph || graph.getCells().length === 0

        // Case 2: Store has data but canvas is empty (useFloorSelection synced graphJson
        // to store, but graph.fromJSON() was never called)
        if (storeHasData && canvasIsEmpty) return true

        // Case 1: Store has no data, check if API has data to sync
        if (storeHasData) return false
        const apiFloor = findMatchingApiFloor(editorMapData, {
          name: fresh.name,
          order: fresh.order,
        })
        return Boolean(apiFloor?.graphJson)
      })()

    console.log(`[useFloorSwitching] check`, {
      floorChanged, lotChanged, lateApiGraphShouldRestore,
      currentFloor, hasEditorMapData: !!editorMapData,
      graphCells: graph?.getCells()?.length ?? 'no graph',
    });

    if (!floorChanged && !lotChanged && !lateApiGraphShouldRestore) {
      return
    }


    // Prevent concurrent floor switches
    if (isFloorSwitchingRef.current) {
      return
    }

    // Skip if graph/paper not ready yet — effect will re-run when they initialize
    // CRITICAL: Do NOT set isFloorSwitchingRef before this check, otherwise the
    // re-run (triggered by graph becoming available) will be blocked by the concurrent guard
    if (!graph || !paper) {
      return
    }

    // Generate a restoration ID to cancel stale operations
    const restorationId = ++floorRestorationIdRef.current

    // Start floor switching
    isFloorSwitchingRef.current = true
    setFloorSwitching(true) // CRITICAL: Block server sync during floor transition
    setIsRestoring(true)

    // Callback for switch start
    onFloorSwitchStart?.()

    // Async restoration logic
    const restoreFloor = async () => {
      try {

        // Get target floor data from freshest Zustand state
        // Using getState() ensures we get the latest persisted data after updateFloorMapData
        const freshFloors = useFloorStore.getState().floors
        let targetFloorData = freshFloors.find(f => f.id === currentFloor)
        if (!targetFloorData) {
          return
        }

        // Verify floor belongs to the current project (lot) to prevent
        // stale restoration from a previous project overwriting cleared state
        if (String(targetFloorData.lotId) !== String(currentLot)) {
          return
        }

        // Check if this restoration is still valid
        if (restorationId !== floorRestorationIdRef.current) {
          return
        }

        // Merge server graphJson into store when local has no meaningful graph (blocked sync during isFloorSwitching).
        const apiFloor = findMatchingApiFloor(editorMapData, {
          name: targetFloorData.name,
          order: targetFloorData.order,
        })
        if (apiFloor?.graphJson && !checkHasLocalData(targetFloorData.mapData?.graphJson)) {
          updateFloorMapData(currentFloor, { graphJson: apiFloor.graphJson })
          const merged = useFloorStore.getState().floors.find((f) => f.id === currentFloor)
          if (merged) {
            targetFloorData = merged
          }
        }

        // Clear graph
        graph.clear()

        // Restore CSV data FIRST (CSV-first rendering strategy)
        const tmd = targetFloorData.mapData
        const csvRawRestore = mapDataGetCsvRaw(tmd)
        if (csvRawRestore) {
          useCsvRuntimeStore.getState().mergeCsvRuntimeForFloor(currentFloor, {
            uploadState: {
              status: 'parsed',
              fileName: mapDataGetCsvFileName(tmd) || 'Restored Data',
              rowCount: (mapDataGetCsvParsed(tmd) as { rowCount?: number } | null)?.rowCount || 0,
              isRestored: true,
            },
            rawData: csvRawRestore,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            parsedData: (mapDataGetCsvParsed(tmd) as any) ?? null,
            groupedLayers: mapDataGetCsvGroupedLayers(tmd),
            selectedLayers: new Set(mapDataGetCsvSelectedLayers(tmd)),
            file: null,
          })

          setLoadedFileName(mapDataGetCsvFileName(tmd) || null)
        } else {
          useCsvRuntimeStore.getState().mergeCsvRuntimeForFloor(currentFloor, {
            uploadState: { status: 'idle' },
            file: null,
            rawData: null,
            parsedData: null,
            groupedLayers: null,
            selectedLayers: new Set<string>(),
          })

          setLoadedFileName(null)
        }

        // Small delay to allow CSV rendering to stabilize
        await new Promise(resolve => setTimeout(resolve, 100))

        // Check if this restoration is still valid
        if (restorationId !== floorRestorationIdRef.current) {
          return
        }

        // Restore graph JSON if exists
        if (targetFloorData.mapData?.graphJson) {
          const cells = targetFloorData.mapData.graphJson.cells
          console.log(`[useFloorSwitching] RESTORING graphJson`, {
            cellCount: Array.isArray(cells) ? cells.length : 'not array',
            graphJsonKeys: Object.keys(targetFloorData.mapData.graphJson),
          });
          try {
            graph.fromJSON(targetFloorData.mapData.graphJson)
            console.log(`[useFloorSwitching] graph.fromJSON OK, cells now:`, graph.getCells().length);

            // 표준 비율 보정 — 일반 주차구역 단변(반포 표준 198.32px) 기준 전체 스케일 일치.
            // paper 도 같이 전달해 view-level scale 자동 보정 (화면 크기는 그대로 유지).
            // idempotent — 이미 표준 크기면 skip.
            const normalizeResult = normalizeMapScale(graph, paper)
            if (normalizeResult.applied) {
              console.log(`[useFloorSwitching] normalize applied`, normalizeResult);
            }
            if (paper) {
              // 복원 직후엔 항상 보이는 컨테이너 크기에 직접 맞춰 수동 fit 한다.
              //
              // - normalize 가 skip 된 경우(이미 표준 스케일): 복원된 graph 가
              //   (scale=1, translate=0) 으로 viewport 밖에 위치해 빈 화면처럼 보인다.
              // - normalize 가 적용된 경우: normalizeMapScale 내부의
              //   paper.scaleContentToFit 이 paper SVG 크기(20000×20000) 기준으로 fit
              //   하므로 실제 컨테이너(≈1066×783)에는 맞지 않아 역시 화면 밖으로 나간다.
              //
              // 두 경우 모두 rAF 로 늦게 실행되는 이 수동 fit 이 최종 transform 을
              // 결정한다. graph.getBBox() 는 model 좌표라 async 렌더 완료 전에도 즉시
              // 정확하다.
              requestAnimationFrame(() => {
                try {
                  const container = paper.el.parentElement
                  const bbox = graph.getBBox()
                  const vw = container?.clientWidth ?? 0
                  const vh = container?.clientHeight ?? 0
                  if (bbox && bbox.width > 0 && bbox.height > 0 && vw > 0 && vh > 0) {
                    const s = Math.min(vw / bbox.width, vh / bbox.height) * 0.9
                    paper.scale(s, s)
                    paper.translate(
                      (vw - bbox.width * s) / 2 - bbox.x * s,
                      (vh - bbox.height * s) / 2 - bbox.y * s,
                    )
                  }
                } catch (e) {
                  // fit 실패는 무시 — 빈 화면이 되더라도 데이터는 보존됨.
                }
              })
            }

            // Rebuild objectsByLayer for the right panel
            if (props.setObjectsByLayer) {
              const restoredCells = graph.getCells()
              const layerMap = new Map<string, dia.Element[]>()
              restoredCells.forEach((cell) => {
                if (cell.isElement()) {
                  const el = cell as dia.Element
                  const layer = el.get('data')?.layer || 'default'
                  const arr = layerMap.get(layer) || []
                  arr.push(el)
                  layerMap.set(layer, arr)
                }
              })
              props.setObjectsByLayer(layerMap)
            }
            if (props.setElementCount) {
              props.setElementCount(graph.getCells().length)
            }
          } catch (error) {
            console.error(`[useFloorSwitching] graph.fromJSON FAILED`, error);
          }
        } else {
          console.log(`[useFloorSwitching] NO graphJson to restore`, {
            hasMapData: !!targetFloorData.mapData,
            graphJsonValue: targetFloorData.mapData?.graphJson,
          });
        }

        // Restore render scale
        const savedScale = targetFloorData.mapData?.renderScale
        if (savedScale) {
          setRenderScale(savedScale)
        } else {
          setRenderScale(1500) // Default scale
        }

        // Minimum loading display time (prevent flicker)
        await new Promise(resolve => setTimeout(resolve, 1000))

        // Check if this restoration is still valid
        if (restorationId !== floorRestorationIdRef.current) {
          return
        }


        // Update previous floor reference
        previousFloorRef.current = currentFloor
      } catch (error) {
      } finally {
        // Only clear restoring state if this is the latest restoration.
        // Delay flag reset by one animation frame so that other effects
        // (useHybridRendering, useGraphAutoSave) still see isFloorSwitching=true
        // during the same React commit that rendered the restored graph.
        if (restorationId === floorRestorationIdRef.current) {
          requestAnimationFrame(() => {
            if (restorationId !== floorRestorationIdRef.current) return
            isFloorSwitchingRef.current = false
            setFloorSwitching(false)
            setIsRestoring(false)
            onFloorSwitchComplete?.()
          })
        }
      }
    }

    // Execute restoration
    restoreFloor()
    // Note: 'floors' removed from dependencies since we now use useFloorStore.getState().floors
    // directly inside restoreFloor() to get the freshest state after updateFloorMapData
    // editorMapData: re-run when API map_data loads so late merge + restore can apply
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // isFloorSwitching removed — it's set inside this effect, causing self-re-trigger.
  // The internal isFloorSwitchingRef guards against double execution.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFloor,
    currentLot,
    editorMapData,
    graph,
    paper,
    setFloorSwitching,
    onFloorSwitchStart,
    onFloorSwitchComplete,
  ])

  return {
    isRestoring,
    renderScale,
    loadedFileName,
    handleRenderScaleChange,
    setRenderScale,
  }
}
